// MA migration and CvcdSwap exit use separate contracts and ledgers.
import { CreateContract, GetGlobalSigner, BN, MAX256, WeiToUnits } from '../../ethers/e'
import MA_ABI from './abi/MA_V2.json'
import SWAP_ABI from './abi/MerchantSwap.json'
import EXIT_ABI from './abi/cvcd-swap.json'
import manifest from './manifest.json'
import { CHAIN_ID, MA_V2, MERCHANT_SWAP, CVCD_SWAP, CVCD_EXIT_ENABLED } from './network'

const contract = (address, abi, signer = GetGlobalSigner()) => CreateContract(address, abi, signer)
const failure = code => Object.assign(new Error(code), { code })

// A fork chain ID alone does not identify the exported deployment.
export async function CheckMigrationNetwork(verifyMerchant = true) {
    const signer = GetGlobalSigner()
    const provider = signer?.provider || signer
    if (!provider || typeof provider.getNetwork !== 'function') throw failure('MERCHANT_NETWORK_MISMATCH')
    const chainId = typeof signer.getChainId === 'function' ? await signer.getChainId() : (await provider.getNetwork()).chainId
    if (chainId !== CHAIN_ID) throw failure('MERCHANT_NETWORK_MISMATCH')
    if (manifest.forkBlockHash) {
        const block = await provider.getBlock(manifest.forkBlockNumber)
        if (block?.hash?.toLowerCase() !== manifest.forkBlockHash.toLowerCase()) throw failure('MIGRATION_DEPLOYMENT_MISMATCH')
    }
    if (verifyMerchant) {
        const slot = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc'
        for (const key of ['ma', 'swap', 'manager']) {
            const implementation = await provider.getStorageAt(manifest.addresses[key], slot)
            if (implementation.slice(-40).toLowerCase() !== manifest.implementations[key].slice(2).toLowerCase()) {
                throw failure('MIGRATION_DEPLOYMENT_MISMATCH')
            }
        }
    }
    return signer
}

export async function MerchantStairData(account) {
    const signer = await CheckMigrationNetwork()
    const swap = contract(MERCHANT_SWAP, SWAP_ABI, signer)
    const ma = contract(MA_V2, MA_ABI, signer)
    const [summary, remaining, legacy, settled, profitsClaimed, conversion, orders, stakes] = await Promise.all([
        swap.getStairSummary(account), swap.getRemainingStairPrincipal(account),
        swap.getLegacyStairPrincipal(account), ma.legacyStairPrincipalSettled(account),
        ma.legacyStairProfitsClaimed(account), swap.stairConversions(account), ma.getOrder(account), swap.getCvcStake(account)
    ])
    // MAX256 marks a new migration, never index a stake with it.
    const isLegacy = conversion.converted && !conversion.stakeId.eq(MAX256)
    const stake = isLegacy ? stakes.find((_, index) => conversion.stakeId.eq(index)) : null
    const cvcRefund = stake && !stake.withdrawn ? stake.amount : BN(0)
    let profit = BN(0), fee = BN(0)
    for (const order of orders) {
        if (order.lastSettleTime.isZero()) continue
        const gross = order.newInvest.gt(order.initialTnvest) ? order.newInvest.sub(order.initialTnvest) : BN(0)
        profit = profit.add(gross)
        fee = fee.add(gross.div(100))
    }
    return {
        totalPrincipal: WeiToUnits(summary.totalPrincipal), remainingPrincipal: WeiToUnits(remaining),
        converted: summary.converted, isLegacy, legacyPrincipal: WeiToUnits(legacy),
        principalSettled: WeiToUnits(settled), profitsClaimed,
        profit: WeiToUnits(profit), fee: WeiToUnits(fee), netProfit: WeiToUnits(profit.sub(fee)),
        cvcRefund: WeiToUnits(cvcRefund),
        needsSettlement: isLegacy && (settled.lt(legacy) || !profitsClaimed || cvcRefund.gt(0))
    }
}
MerchantStairData.Type = {
    totalPrincipal: '0', remainingPrincipal: '0', converted: false, isLegacy: false,
    legacyPrincipal: '0', principalSettled: '0', profitsClaimed: false,
    profit: '0', fee: '0', netProfit: '0', cvcRefund: '0', needsSettlement: false
}

async function submit(address, abi, method, check = async () => {}) {
    const signer = await CheckMigrationNetwork(address !== CVCD_SWAP)
    const instance = contract(address, abi, signer)
    await instance.callStatic[method]()
    await check()
    if (GetGlobalSigner() !== signer) throw failure('MERCHANT_ACCOUNT_CHANGED')
    await CheckMigrationNetwork(address !== CVCD_SWAP)
    await check()
    return instance[method]()
}
export const MigrateStair = check => submit(MERCHANT_SWAP, SWAP_ABI, 'migrateStair', check)
export const SettleLegacyStair = check => submit(MERCHANT_SWAP, SWAP_ABI, 'settleLegacyStair', check)

function requireExitEnabled() {
    if (!CVCD_EXIT_ENABLED || !manifest.features?.cvcdExit?.enabled) throw failure('CVCD_EXIT_DISABLED')
}
export async function CvcdExitData(account) {
    requireExitEnabled()
    const signer = await CheckMigrationNetwork(false)
    const swap = contract(CVCD_SWAP, EXIT_ABI, signer)
    const [refund, claimed, stakes, releases] = await Promise.all([
        swap.getExitRefund(account), swap.exitClaimed(account), swap.stakeOrders(account), swap.usdtReleaseOrders(account)
    ])
    return {
        cvc: WeiToUnits(refund.cvc), cvcd: WeiToUnits(refund.cvcd), claimed,
        stakes: stakes.map((order, id) => ({ id, amount: WeiToUnits(order.amount), claimed: order.claimed })),
        releases: releases.map((order, id) => ({ id, amount: WeiToUnits(order.amount), released: WeiToUnits(order.released) }))
    }
}
CvcdExitData.Type = { cvc: '0', cvcd: '0', claimed: false, stakes: [], releases: [] }
export async function ClaimCvcdExit(check) {
    requireExitEnabled()
    return submit(CVCD_SWAP, EXIT_ABI, 'claimExitRefund', check)
}
